import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Table } from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  Columns3,
  Keyboard,
  RotateCcw,
  Settings2,
  SortDesc,
} from 'lucide-react';
import { useState } from 'react';
import TableColumnSortingList from './TableColumnSortingList';
import TableColumnsOrderAndVisibilityList from './TableColumnsOrderAndVisibilityList';
import { TableKeyboardShortcutsList } from './TableKeyboardShortcuts';
import { cn } from '@/lib/utils';

export type ContentState = 'menu' | 'columns' | 'sorting' | 'shortcuts';

interface TableControlsButtonProps<TData> {
  table: Table<TData>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  contentState?: ContentState;
  onContentStateChange?: (state: ContentState) => void;
  onReset?: () => void;
}

export function TableControlsButton<TData>({
  table,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  contentState: controlledContentState,
  onContentStateChange: controlledOnContentStateChange,
  onReset,
}: TableControlsButtonProps<TData>) {
  // Use controlled state if provided, otherwise use internal state
  const [internalOpen, setInternalOpen] = useState(false);
  const [internalContentState, setInternalContentState] =
    useState<ContentState>('menu');

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen =
    controlledOnOpenChange !== undefined
      ? controlledOnOpenChange
      : setInternalOpen;

  const contentState =
    controlledContentState !== undefined
      ? controlledContentState
      : internalContentState;
  const setContentState =
    controlledOnContentStateChange !== undefined
      ? controlledOnContentStateChange
      : setInternalContentState;

  const showMenu = contentState === 'menu';
  const showContent = contentState !== 'menu';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          aria-label="Table settings"
        >
          <Settings2 className="size-4" />
          <span className="hidden sm:inline">Table</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className={cn('p-1', showContent && 'w-80')}
        onCloseAutoFocus={() => setContentState('menu')}
        onFocusOutside={(e) => showContent && e.preventDefault()}
      >
        {showMenu && (
          <div className="space-y-0.5">
            <button
              onClick={() => setContentState('sorting')}
              className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
            >
              <SortDesc className="size-4" />
              <span className="flex-1 text-left">Sort</span>
              <ChevronRight className="text-muted-foreground size-4" />
            </button>
            <button
              onClick={() => setContentState('columns')}
              className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
            >
              <Columns3 className="size-4" />
              <span className="flex-1 text-left">Columns</span>
              <ChevronRight className="text-muted-foreground size-4" />
            </button>
            <button
              onClick={() => setContentState('shortcuts')}
              className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
            >
              <Keyboard className="size-4" />
              <span className="flex-1 text-left">Keyboard shortcuts</span>
              <ChevronRight className="text-muted-foreground size-4" />
            </button>
            {onReset && (
              <>
                <div className="bg-border my-1 h-px" />
                <button
                  onClick={() => {
                    onReset();
                    setOpen(false);
                  }}
                  className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
                >
                  <RotateCcw className="size-4" />
                  <span className="flex-1 text-left">Reset to defaults</span>
                </button>
              </>
            )}
          </div>
        )}

        {showContent && (
          <div className="p-3">
            <button
              onClick={() => setContentState('menu')}
              className="text-muted-foreground hover:text-foreground mb-3 flex items-center gap-1 text-xs"
            >
              <ChevronLeft className="size-3" />
              Back
            </button>
            {contentState === 'columns' && (
              <TableColumnsOrderAndVisibilityList table={table} />
            )}
            {contentState === 'sorting' && (
              <TableColumnSortingList table={table} />
            )}
            {contentState === 'shortcuts' && <TableKeyboardShortcutsList />}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
